import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { translationKeys } from "../../../models/app-translation-keys";
import { useImageGenerationService } from "../../../services/ServiceHooks/imageGenerationHook";
import { IThumbnailsMap } from "../../../stores";
import { useStore } from "../../../stores/StoreContext";
import { ImageCard } from "../ImageCard/ImageCard";

import "./ImageGrid.scss";

import { observer } from "mobx-react-lite";

import { Button } from "primereact/button";

import { downloadBlobFile } from "../../../../utils/functions/downloadBlobFile";
import { getLocalDateTimeStamp } from "../../../../utils/functions/getLocalDateTimeStamp";
import { PossibleFileTypesEnum } from "../../../components/Shared/Models/Constants/wellKnownFileTypes.const";

const featureTranslationKeys = translationKeys.feature.imageGeneration;

export const ImageGrid: FunctionComponent = observer(() => {
  const { t } = useTranslation();
  const { imageGenerationStore, globalErrorsStore } = useStore();
  const imageGenService = useImageGenerationService();
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());
  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);

  const disabled = isDownloadInProgress;
  const loading =
    isDownloadInProgress || imageGenerationStore.loadingImageIds || imageGenerationStore.loadingThumbnails;

  useEffect(() => {
    handleDeselectAll();

    if (
      imageGenerationStore.generatedImageIds?.length &&
      !hasLoadedThumbnails(imageGenerationStore.generatedImageIds)
    ) {
      loadThumbnails(imageGenerationStore.generatedImageIds);
    }
  }, [imageGenerationStore.generatedImageIds, imageGenerationStore.thumbnailsMap]);

  const hasLoadedThumbnails = (imageIds: Array<string>): boolean => {
    const thumbnailsMap = imageGenerationStore.thumbnailsMap;

    return !!thumbnailsMap && imageIds.every(imageId => !!thumbnailsMap[imageId]);
  };

  const handleSelectImage = (imageId: string) => {
    if (disabled) {
      return;
    }

    const newSet = new Set(selectedImageIds);
    if (newSet.has(imageId)) {
      newSet.delete(imageId);
    } else {
      newSet.add(imageId);
    }
    setSelectedImageIds(newSet);
  };

  const handleSelectAll = () => {
    if (disabled) {
      return;
    }

    if (imageGenerationStore.generatedImageIds?.length) {
      const allImageIds = new Set(imageGenerationStore.generatedImageIds);
      setSelectedImageIds(allImageIds);
    }
  };

  const handleDeselectAll = () => {
    setSelectedImageIds(new Set());
  };

  const handleDownloadSelected = async (singleImageId?: string, extension = PossibleFileTypesEnum.png) => {
    try {
      globalErrorsStore.removeError(featureTranslationKeys.errors.downloadFailed);
      const selectedImageIdsList = singleImageId ? [singleImageId] : Array.from(selectedImageIds);
      const contentTypePrefix = "generatedImage";
      const fallbackFileName = `${t(featureTranslationKeys.downloadImageNamePrefix)}${getLocalDateTimeStamp()}.${extension}`;

      if (!selectedImageIdsList.length && !singleImageId) {
        return;
      }

      setIsDownloadInProgress(true);

      const response = (await imageGenService.getImagesForDownload(selectedImageIdsList)).raw;

      downloadBlobFile(await response.blob(), fallbackFileName, response, contentTypePrefix);
    } catch (error) {
      console.error("[ImageGrid] Error downloading selected images", error);
      globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.downloadFailed);
    } finally {
      setIsDownloadInProgress(false);
    }
  };

  const loadThumbnails = async (imageIds: Array<string>): Promise<void> => {
    const thumbnails: IThumbnailsMap = {};

    if (!imageIds?.length) {
      imageGenerationStore.setGeneratingImages(false);
      globalErrorsStore.removeError(featureTranslationKeys.errors.requestFailed);
      return;
    }

    const fetchImagePromises = imageIds.map(async imageId => {
      try {
        const blob = await imageGenService.getThumbnailsByImageId([imageId]);

        if (!blob) {
          console.error(`[ImageGrid] Thumbnail fetch for ${imageId} returned: ${blob}`);
          return;
        }

        thumbnails[imageId] = blob;
        return blob;
      } catch (error: any) {
        console.error(`[ImageGrid] Failed to load thumbnail for ${imageId}`, error);
      }
    });

    // Wait for all fetches to settle
    try {
      imageGenerationStore.setLoadingThumbnails(true);
      globalErrorsStore.removeError(featureTranslationKeys.errors.requestFailed);

      await Promise.all(fetchImagePromises);
      imageGenerationStore.setThumbnailMapToActiveProcess({
        ...thumbnails,
      });
    } catch (error: any) {
      console.error("[ImageGrid] Error loading thumbnails: ", error);
      globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.requestFailed);
    } finally {
      imageGenerationStore.setLoadingThumbnails(false);

      imageGenerationStore.setGeneratingImages(false);
    }
  };

  if (loading && !isDownloadInProgress) {
    return (
      <div className="image-grid-container">
        <div className="image-grid disabled">
          {Array.from({ length: imageGenerationStore.imageCount }, (_, i) => (
            <ImageCard
              imageCount={1}
              key={i}
              loading={true}
              disabled={false}
              error={globalErrorsStore.hasSpecificErrors([featureTranslationKeys.errors.requestFailed])}
            />
          ))}
        </div>
      </div>
    );
  }

  const activeThumbnails = Object.entries(imageGenerationStore?.activeProcess?.thumbnailsMap || {});

  if (!activeThumbnails.length) {
    return <div className="image-grid empty">{t(featureTranslationKeys.emptyListText)}</div>;
  }

  return (
    <div className="image-grid-container">
      {activeThumbnails.length > 1 && (
        <div className="image-grid-controls">
          <div className="controls-left">
            <Button
              label={t(featureTranslationKeys.deselectAllButton)}
              data-testid="image-grid-deselect-all-button"
              onClick={handleDeselectAll}
              disabled={disabled}
              outlined
              size="small"
            />
          </div>

          <div className="controls-right">
            <Button
              label={t(featureTranslationKeys.selectAllButton)}
              data-testid="image-grid-select-all-button"
              onClick={handleSelectAll}
              disabled={disabled}
              outlined
              size="small"
            />
          </div>
        </div>
      )}

      <div className="image-grid">
        {activeThumbnails.map(([imageId, thumbnailSrc]) => (
          <ImageCard
            imageCount={activeThumbnails.length || imageGenerationStore.imageCount}
            key={imageId}
            objectId={imageId}
            blob={thumbnailSrc}
            loading={loading && !thumbnailSrc}
            disabled={disabled}
            error={globalErrorsStore.hasSpecificErrors([featureTranslationKeys.errors.requestFailed])}
            isSelected={selectedImageIds.has(imageId)}
            onSelect={() => activeThumbnails.length > 1 && handleSelectImage(imageId)}
            handleDownload={handleDownloadSelected}
          />
        ))}
      </div>

      {activeThumbnails.length > 1 && selectedImageIds.size > 0 && (
        <div className="image-grid-footer">
          <Button
            label={t(featureTranslationKeys.downloadSelectedButton, { selectedImagesCount: selectedImageIds.size })}
            data-testid="image-grid-download-selected-button"
            onClick={() => handleDownloadSelected(undefined, PossibleFileTypesEnum.zip)}
            disabled={imageGenerationStore.isGeneratingImages}
            className="download-selected-btn"
          />
        </div>
      )}
    </div>
  );
});
